import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import styled, { css, useTheme } from "styled-components";
import Label from "~/components-shared/Label";
import { Button, Icon, Text } from "~/components-shared";
import EmojiDropdown from "~/components-shared/EmojiDropdown";
import { genericColor } from "~/lib/resolvers";
const Wrapper = styled.div`
  ${_ref => {
  let {
    $maxWidth
  } = _ref;
  return $maxWidth && `max-width: ${$maxWidth}`;
}};
  ${_ref2 => {
  let {
    $fullWidth
  } = _ref2;
  return $fullWidth && "width: 100%";
}};
  ${_ref3 => {
  let {
    $s,
    theme
  } = _ref3;
  return theme.spacing.resolve(theme, $s);
}};
`;
const InputWrapper = styled.div`
  position: relative;
  background-color: ${_ref4 => {
  let {
    theme,
    $color
  } = _ref4;
  return genericColor(theme, $color);
}};
  backdrop-filter: blur(4px);
  padding: 0 1.1rem;
  width: 100%;
  border: none;
  border-radius: ${_ref5 => {
  let {
    $rounded,
    theme
  } = _ref5;
  return $rounded ? "2rem" : theme.shape.borderRadius.primary;
}};
  display: grid;
  grid-template-columns: ${_ref6 => {
  let {
    $template
  } = _ref6;
  return $template;
}};
  overflow: hidden;
  align-items: center;
  -webkit-tap-highlight-color: transparent;
  transition: box-shadow 200ms ease-in;
  box-shadow: rgb(255 255 255) 0 0 0 0, transparent 0 0 0 1px;
  ${_ref7 => {
  let {
    $outline,
    $hasError,
    theme
  } = _ref7;
  return theme.shadows.outline(theme, $outline, $hasError ? theme.colors.danger.main : theme.colors.primary.main);
}}
  z-index: 1;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: background-color 200ms ease-out;
    background-color: transparent;
    border-radius: ${_ref8 => {
  let {
    $rounded,
    theme
  } = _ref8;
  return $rounded ? "2em" : theme.shape.borderRadius.primary;
}};
    z-index: -1;
  }

  ${_ref9 => {
  let {
    $disabled
  } = _ref9;
  return !$disabled && css`
      &:hover:before {
        background-color: ${_ref10 => {
    let {
      theme
    } = _ref10;
    return theme.colors.action.hover;
  }};
      }
    `;
}}
`;
const Input = styled.input`
  border: none;
  background-color: transparent;
  ${_ref11 => {
  let {
    theme
  } = _ref11;
  return `font-size: ${4 * theme.typography.fontStep}rem`;
}};
  ${_ref12 => {
  let {
    theme
  } = _ref12;
  return `line-height: ${4 * theme.typography.fontStep}rem`;
}};
  font-weight: ${_ref13 => {
  let {
    theme
  } = _ref13;
  return theme.typography.fontWeight.regular;
}};
  padding: 0.75rem 0;
  width: 100%;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${_ref14 => {
  let {
    theme
  } = _ref14;
  return theme.colors.text.secondary;
}};
  }
`;
const Error = styled.span`
  font-size: 0.8em;
  display: inline-block;
  margin-left: 0.5em;
  color: ${_ref15 => {
  let {
    theme
  } = _ref15;
  return theme.colors.danger.main;
}};
`;
const ElementAfter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const InputText = _ref16 => {
  let {
    label,
    hasError = false,
    errorMessage,
    color = "translucent",
    required = false,
    disabled = false,
    rounded = false,
    prefix,
    outline,
    value,
    onChange = () => false,
    type = "text",
    clearButton = false,
    emojiPicker = false,
    elemBefore,
    elemAfter,
    fullWidth = false,
    maxWidth,
    s,
    labelProps,
    children,
    ...rest
  } = _ref16;
  let template = "auto";
  const theme = useTheme();
  const inputValue = prefix && typeof value === "string" ? value.replace(prefix, "") : value;
  const handleChange = e => {
    onChange(prefix ? `${prefix}${e.target.value}` : e.target.value);
  };
  const handleClear = e => {
    e.stopPropagation();
    onChange("");
  };
  const elementAfter = /*#__PURE__*/React.createElement(ElementAfter, null, emojiPicker && /*#__PURE__*/React.createElement(EmojiDropdown, {
    onChange: emoji => onChange(`${value}${emoji}`)
  }), clearButton && value && /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      p: 1
    },
    outline: false,
    rounded: true,
    onClick: handleClear
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "cross",
    display: "block",
    color: theme.colors.text.secondary
  })), elemAfter);
  if (elemBefore && elementAfter) template = "auto 1fr auto";
  if (elemBefore && !elementAfter) template = "auto 1fr";
  if (!elemBefore && elementAfter) template = "1fr auto";
  return /*#__PURE__*/React.createElement(Wrapper, {
    $s: s,
    $fullWidth: fullWidth,
    $maxWidth: maxWidth
  }, !!label && /*#__PURE__*/React.createElement(Label, _extends({
    required: required,
    disabled: disabled,
    s: {
      mb: 2
    }
  }, labelProps), label), /*#__PURE__*/React.createElement(InputWrapper, {
    $hasError: hasError,
    $template: template,
    $rounded: rounded,
    $disabled: disabled,
    $color: color,
    $outline: outline
  }, elemBefore, !!prefix && /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    lineHeight: 4
  }, prefix), /*#__PURE__*/React.createElement(Input, _extends({
    type: type,
    value: inputValue,
    onChange: handleChange,
    disabled: disabled
  }, rest)), elementAfter), errorMessage && /*#__PURE__*/React.createElement(Error, null, errorMessage));
};
export default InputText;